
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import useDetails from "../../hooks/useDetails";
import api from "../../api/api";
import { toast } from "react-toastify";
import { LuArrowRight, LuArrowUpRight } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { FiHelpCircle } from "react-icons/fi";
import moment from "moment";
import { getHostelList, hostelError, bedDescription, hostelMatchesStudentGender } from "../../utils/hostel";
import { currencyFormatter } from "../../utils";

/**
 * Bed description helper.
 * The API only returns a label (A, B, C, D...). The client wants a human
 * description (A - Upper bunk, B - Lower bunk, C - Upper bunk, D - Lower bunk).
 * If the backend later adds a `description` field on the bed space, it wins.
 * Otherwise: A/C/E... => Upper, B/D/F... => Lower, even when bed lists are filtered.
 */
const getBedDescription = bedDescription;

const getBedStatus = (bed) => {
  if (bed?.is_occupied) return "occupied";
  if (bed?.is_reserved) return "reserved";
  return "available";
};

const STATUS_STYLES = {
  available:
    "border-green-300 bg-green-50 text-green-800 hover:border-green-500 cursor-pointer",
  occupied: "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed",
  reserved: "border-amber-200 bg-amber-50 text-amber-700 cursor-not-allowed",
};

const Accomodation = () => {
  const [selectedSession, setSelectedSession] = useState("");
  const [selectedHostel, setSelectedHostel] = useState("");
  const [selectedRoom, setSelectedRoom] = useState("");
  const [selectedBed, setSelectedBed] = useState("");
  const [rooms, setRooms] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [records, setRecords] = useState([]);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [btn, setBtn] = useState("Book");

  const { data: details, loading: detailsLoading } = useDetails();

  const navigate = useNavigate();

  const schoolFee = details?.payment_complete;
  const [fetchError, setFetchError] = useState("");
  const [fetching, setFetching] = useState(true);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const roomRequest = useRef(0);
  const refreshRequest = useRef(0);
  const refreshInFlight = useRef(false);
  const previousApplications = useRef(null);
  const currentSession = availableSessions.find(session => String(session.id) === selectedSession)?.id;
  const blockingApplication = records?.find(record =>
    Number(record.session?.id || record.session) === Number(currentSession) &&
    (record.status?.toUpperCase() === "APPROVED" || record.status?.toUpperCase() === "PENDING" || record.revocation_kind === "MANUAL")
  );
  const fullyPaid = schoolFee === "full";
  const canBook = 
    // fullyPaid && 
    !!currentSession && 
    !blockingApplication &&
    !fetching && !fetchError;
  const bookingUnavailableReason = !fullyPaid
    ? "Pay your school fees in full for this session before applying. Partial payment does not qualify."
    : fetchError
    ? "Application information could not be loaded. Use Refresh above to retry."
    : fetching
    ? "Loading application information..."
    : !currentSession
    ? "Select an academic session before choosing your hostel and bed space."
    : blockingApplication
    ? `You already have an application for this session (${blockingApplication.status}). Check your application below.`
    : "";

  const selectedRoomData = useMemo(
    () => rooms?.find((r) => r.id === Number(selectedRoom)),
    [rooms, selectedRoom]
  );

  const selectedBedData = useMemo(() => {
    const beds = selectedRoomData?.bed_spaces || [];
    const idx = beds.findIndex((b) => b.id === Number(selectedBed));
    return idx === -1 ? null : { ...beds[idx], description: getBedDescription(beds[idx], idx) };
  }, [selectedRoomData, selectedBed]);

  const refresh = useCallback(async (background = false) => {
    if (background && refreshInFlight.current) return;
    refreshInFlight.current = true;
    const request = ++refreshRequest.current;
    if (!background) setFetching(true);
    setFetchError("");
    try {
      const results = await Promise.allSettled([
        getHostelList("/hostel/hostels/"),
        getHostelList("/hostel/applications/"), getHostelList("/portal/academic-sessions/")
      ]);
      if (request !== refreshRequest.current) return;
      const [hostelResult, applicationResult, sessionResult] = results;
      if (hostelResult.status === "fulfilled") setHostels(hostelResult.value);
      if (sessionResult.status === "fulfilled") setAvailableSessions(sessionResult.value);
      if (applicationResult.status === "fulfilled") {
      const applications = applicationResult.value.map(record => ({ ...record, status: String(record.status || "").toUpperCase() }));
      if (previousApplications.current) {
        applications.forEach(record => {
          const previous = previousApplications.current.find(row => row.id === record.id);
          if (!previous) toast.info(`A new hostel allocation is available: ${record.hostel?.name}. Check your applications below.`);
          if (previous && (previous.status !== record.status || previous.is_paid !== record.is_paid)) {
            toast.info(`Accommodation application #${record.id}: ${record.status}${record.is_paid ? " — payment confirmed" : ""}`);
          }
        });
      }
      previousApplications.current = applications;
      setRecords(applications);
      }
      const failed = results.find(result => result.status === "rejected");
      if (failed) setFetchError(hostelError(failed.reason));
    } catch (error) {
      if (request === refreshRequest.current) setFetchError(hostelError(error));
    } finally {
      if (request === refreshRequest.current) { setFetching(false); refreshInFlight.current = false; }
    }
  }, []);

  const getRooms = useCallback(async () => {
    const request = ++roomRequest.current;
    setRooms([]);
    if (!selectedHostel || !currentSession) { setRoomsLoading(false); return; }
    setRoomsLoading(true);
    try {
      const rows = await getHostelList(`/hostel/rooms/?hostel_id=${selectedHostel}&session_id=${currentSession}&page_size=200`);
      if (request === roomRequest.current) setRooms(rows);
    } catch (error) {
      if (request === roomRequest.current) toast.error(hostelError(error));
    } finally {
      if (request === roomRequest.current) setRoomsLoading(false);
    }
  }, [selectedHostel, currentSession]);

  const payForAccommodation = async (application_id, hostel_id) => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `/hostel/applications/${application_id}/pay/`,
        {
          hostel: hostel_id,
        }
      );
      const { payment_link } = data;
      if (!payment_link) throw new Error("Missing payment link");
      toast.success("Initiating payment");
      window.location.replace(payment_link);
    } catch (error) {
      console.log(error);
      setLoading(false);
      toast.error(
        hostelError(error)
      );
    }
  };

  const resetSelection = () => {
    setSelectedHostel("");
    setSelectedRoom("");
    setSelectedBed("");
  };

  const handleAccommodationBooking = async (e) => {
    e.preventDefault();
    if (!canBook || loading || roomsLoading) return toast.warn("Booking is unavailable. Check your eligibility and application status.");
    if (!selectedBedData || getBedStatus(selectedBedData) !== "available") return toast.warn("Select an available bed space.");

    if (!hostelMatchesStudentGender(hostels.find(h => h.id === Number(selectedHostel)), details?.gender)) return toast.warn("Select a hostel that matches your gender.");
    if (!selectedHostel) return toast.warn("Please select a hostel to proceed");
    if (!selectedRoom) return toast.warn("Please select a room to proceed");
    if (!selectedBed) return toast.warn("Please select a bed space to proceed");

    const confirm = window.confirm(
      `Are you sure you want to book this accommodation?\n
       Hostel: ${hostels?.find((h) => h.id === Number(selectedHostel))?.name}
       Session: ${availableSessions.find(session => session.id === currentSession)?.name}
       Room: ${selectedRoomData?.room_number}
       Bed space: ${selectedBedData?.label} (${selectedBedData?.description})`
    );

    if (!confirm) return;

    setLoading(true);
    setBtn("Loading...");
    try {
      await api.post(`/hostel/applications/`, {
        hostel: Number(selectedHostel),
        room: Number(selectedRoom),
        bed_space: Number(selectedBed),
        session: Number(currentSession),
      });
      toast.success("Booking successful!");
      setBtn("Book");
      resetSelection();
      // refresh so the table, beds and booked records reflect the new booking
      await refresh();
    } catch (error) {
      console.log(error);
      setBtn("Failed, try again!");
      setSelectedBed("");
      toast.error(
        hostelError(error)
      );
      // bed may have just been taken by someone else
      getRooms();
      refresh();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    return () => { refreshRequest.current++; };
  }, [refresh]);

  useEffect(() => {
    const update = () => { if (document.visibilityState === "visible") refresh(true); };
    const timer = window.setInterval(update, 5000);
    window.addEventListener("focus", update);
    document.addEventListener("visibilitychange", update);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", update); document.removeEventListener("visibilitychange", update); };
  }, [refresh]);

  useEffect(() => {
    if (!selectedHostel) return;
    getRooms();
    return () => { roomRequest.current++; };
  }, [getRooms, selectedHostel]);

  const bedLabel = (record) =>
    record?.bedspace?.label ||
    record?.bed_space?.label ||
    record?.bed?.label ||
    "—";

  return (
    <div className="">
      {!details && <p role="status">{detailsLoading ? "Loading student profile..." : "Your student profile could not be loaded. Refresh the page to retry."}</p>}
      {details && (
        <div className="space-y-4">
          <div className="flex gap-4 md:items-center justify-between flex-col md:flex-row">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-brand-darkGray">
                Hostel Accommodation{" "}
              </h1>
              <p className="text-sm text-gray-400">Apply for a bed space and track your accommodation. Direct allocations appear automatically with payment available.</p>
            </div>

            <button
              className="w-fit bg-[#5D71C0] py-2 px-3 flex items-center gap-9 border border-[#E2D49A80] rounded-xl hover:bg-[#5D71C0]/95 hover:scale-105 transition-all ease-in-out"
              onClick={() => navigate("/accommodation/history")}
            >
              <p className="text-sm font-medium text-white">Accommodation data</p>
              <div className="size-9 bg-[#1E2F733B] rounded-lg flex justify-center items-center cursor-pointer group">
                <LuArrowUpRight className="size-[18px] text-white group-hover:scale-125 transition-all ease-in-out" />
              </div>
            </button>
          </div>

          {/* {!fullyPaid && (
            <div className="w-full md:w-3/4 px-4 py-3 bg-[#FFF2B82E] border border-[#7168412B] text-sm font-semibold text-[#4A4117CC] rounded-xl flex items-center gap-3">
              <FiHelpCircle className="size-5 text-[#DACA84] flex-none" />
              You must pay your school fees in full for this session to apply for accommodation. Partial payment does not qualify.
            </div>
          )} */}

          <div className="bg-white rounded-xl card-body space-y-10">
            <button type="button" onClick={() => { refresh(); getRooms(); }} disabled={fetching || loading} className="btn btn-primary">Refresh availability and applications</button>
            {fetching && <p role="status">Loading accommodation...</p>}
            {fetchError && <p role="alert" className="text-red-600">{fetchError} Use Refresh to retry.</p>}
            {blockingApplication && <p className="text-amber-800">You already have an application for this session ({blockingApplication.status}). See its status below. Contact the hostel office for changes.</p>}
            {records?.length > 0 && (
              <section className="space-y-4" aria-label="Your hostel applications">
                <h2 className="text-lg font-semibold">Your hostel applications</h2>
                {records.map(record => (
                  <article key={record.id} className="rounded-xl border border-gray-200 p-4 space-y-3">
                    <h3 className="font-semibold">{record.hostel?.name} — {record.status}</h3>
                    {record.status === "APPROVED" && !record.is_paid && <p className="text-sm text-green-800">Your application has been approved. Proceed to payment to secure your allocation before the deadline.</p>}
                    <p className="text-sm">Room: {record.room?.room_number || "—"} · Bed: {bedLabel(record)} · Session: {availableSessions.find(session => session.id === record.session)?.name || "—"}</p>
                    <p className="text-sm">Fee: {record.hostel?.fee != null ? currencyFormatter(record.hostel.fee) : "Awaiting"} · Payment: {record.is_paid ? "Paid" : "Pending"}</p>
                    {record.rejection_reason && <p className="text-sm text-red-700">Reason: {record.rejection_reason}</p>}
                    {record.revocation_kind && <p className="text-sm">Revocation: {record.revocation_kind.toLowerCase()}</p>}
                    {record.payment_review_required && <p className="text-sm text-amber-800">Payment requires review by the hostel office.</p>}
                    {record.payment_deadline && !record.is_paid && record.status === "APPROVED" && <p className="text-sm">Payment deadline: {moment(record.payment_deadline).format("DD MMM YYYY, h:mm A")}{Date.now() >= new Date(record.payment_deadline).getTime() ? " (expired — contact the hostel office)" : ""}</p>}
                    <div className="flex flex-wrap gap-3">
                      {record.status === "APPROVED" && <button className="btn btn-primary" onClick={() => navigate(`/accommodation/allocation?application=${record.id}`)}>Allocation slip</button>}
                      {record.status === "APPROVED" && !record.is_paid && <button className="px-5 py-3 rounded-lg bg-green-600 text-white font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed" onClick={() => payForAccommodation(record.id, record.hostel.id)} disabled={loading || record.payment_review_required || (record.bed_space?.is_reserved && !record.payment_activated_at) || (record.payment_deadline && Date.now() >= new Date(record.payment_deadline).getTime())}>{loading ? "Please wait..." : "Proceed to hostel payment"}</button>}
                      {record.is_paid && <button className="btn btn-primary" onClick={() => navigate("/accommodation/history")}>Go to receipt</button>}
                    </div>
                  </article>
                ))}
              </section>
            )}

            {/* ---------- BOOKING FORM: HOSTEL > ROOM > BED SPACE ---------- */}
            <section aria-labelledby="hostel-application-title">
              <h2 id="hostel-application-title" className="text-lg font-semibold">Apply for Hostel Accommodation</h2>
              {/* {bookingUnavailableReason && <p role="status" className="mt-2 text-sm text-amber-800">{bookingUnavailableReason}</p>} */}
              {/* {!fullyPaid && <button className="btn btn-primary mt-3" onClick={() => navigate("/payments")}>Pay school fees</button>} */}
              <form
                id="booking-form"
                onSubmit={handleAccommodationBooking}
                className="space-y-4 p-2"
              >
                <div className="space-y-2">
                  <label htmlFor="hostel-session" className="text-sm font-medium text-brand-darkGray">Academic session</label>
                  <select id="hostel-session" className="form-control w-full md:w-1/3" value={selectedSession} disabled={fetching || loading || !!fetchError} required onChange={event => {
                    setSelectedSession(event.target.value);
                    resetSelection();
                    setRooms([]);
                  }}>
                    <option value="">--Select academic session--</option>
                    {availableSessions.map(session => <option key={session.id} value={session.id}>{session.name}</option>)}
                  </select>
                  <p className="text-sm text-gray-500">School fees must be paid in full for the session you select.</p>
                </div>
                <fieldset disabled={!canBook || loading} className="space-y-4">
                <h3 className="text-lg font-semibold">
                  Select the hostel, room and bed space you want
                </h3>

                <div className="space-y-2">
                  <label htmlFor="hostel-select" className="text-sm font-medium text-brand-darkGray">
                    Hostel
                  </label>
                  <select
                    id="hostel-select"
                    className="form-control w-full md:w-1/3"
                    value={selectedHostel}
                    onChange={(e) => {
                      setSelectedHostel(e.target.value);
                      setSelectedRoom("");
                      setSelectedBed("");
                    }}
                    required
                  >
                    <option value="">--Select Hostel--</option>
                    {hostels?.filter(h => hostelMatchesStudentGender(h, details.gender)).map((h) => (
                      <option key={h?.id} value={h?.id}>
                        {h?.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="hostel-room" className="text-sm font-medium text-brand-darkGray">
                    Room
                  </label>
                  <select
                    id="hostel-room"
                    className="form-control w-full md:w-1/3"
                    value={selectedRoom}
                    onChange={(e) => {
                      setSelectedRoom(e.target.value);
                      setSelectedBed("");
                    }}
                    required
                    disabled={!selectedHostel || roomsLoading}
                  >
                    <option value="">--Select room--</option>
                    {rooms?.map((r) => (
                      <option
                        key={r?.id}
                        value={r?.id}
                        disabled={r?.available_beds <= 0}
                      >
                        {`Room ${r?.room_number} — ${r?.available_beds}/${r?.bed_space} beds free — ${currencyFormatter(r?.hostel?.fee)}${
                          r?.available_beds <= 0 ? " (Full)" : ""
                        }`}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedRoomData && (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 md:w-2/3">
                      <label className="text-sm font-medium text-brand-darkGray">
                        Bed space
                      </label>
                      <span className="text-xs text-gray-500">
                        Occupants: {selectedRoomData?.occupied_beds ?? 0} /{" "}
                        {selectedRoomData?.bed_space}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:w-2/3">
                      {selectedRoomData?.bed_spaces?.map((bed, idx) => {
                        const status = getBedStatus(bed);
                        const selectable = status === "available";
                        const isSelected = Number(selectedBed) === bed?.id;
                        return (
                          <button
                            key={bed?.id}
                            type="button"
                            disabled={!selectable}
                            onClick={() => setSelectedBed(String(bed?.id))}
                            title={
                              status === "reserved" && bed?.reserved_note
                                ? bed.reserved_note
                                : undefined
                            }
                            className={`rounded-xl border p-3 text-left transition-all ${
                              STATUS_STYLES[status]
                            } ${
                              isSelected
                                ? "ring-2 ring-[#5D71C0] border-[#5D71C0] bg-[#5D71C0]/10"
                                : ""
                            }`}
                          >
                            <div className="text-base font-bold">Bed {bed?.label}</div>
                            <div className="text-xs">{getBedDescription(bed, idx)}</div>
                            <div className="text-[11px] mt-1 capitalize">
                              {isSelected ? "Selected" : status}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <button
                  className="bg-[#5D71C0] py-3 px-4 flex items-center gap-9 border border-[#E2D49A80] rounded-xl hover:bg-[#5D71C0]/95 hover:scale-105 transition-all ease-in-out text-white text-sm font-bold disabled:opacity-60 disabled:hover:scale-100"
                  type="submit"
                  disabled={loading || roomsLoading || !canBook || !selectedBed}
                >
                  {btn === "Book" ? "Submit hostel application" : btn}
                  <LuArrowRight className="size-[18px] text-white" />
                </button>
                </fieldset>
              </form>
            </section>


          </div>
        </div>
      )}
    </div>
  );
};

export default Accomodation;
